import i18n from 'i18next'
import { NavigateFunction } from 'react-router-dom'
import {
	createUserApi,
	deleteSignatureApi,
	deleteUserApi,
	getAllUsers,
	getPracticeUsersApi,
	getStaffUsers,
	getUserByUsernameAdminApi,
	getUserByUsernameApi,
	saveSignatureApi,
	setUserAvailabilityApi,
	updateUserApi,
	updateUserIcdsApi,
	updateUserInStoreFlagApi,
} from '../../apiCalls'
import {
	checkStoreIdFormat,
	EXTERNAL_IDENTIFIER_SOURCE,
} from '../../libs/utils'
import { Id } from '../../model/model'
import {
	ApiUser,
	CreateUserPayload,
	UpdateUserPayload,
	User,
} from '../../model/users'
import {
	AppThunk,
	AppThunkPromise,
	TeloDispatch,
	TeloGetState,
} from '../../store'
import appActions from '../app/actions'
import { selectStoreId, selectStorePracticeExternalId } from '../app/selectors'
import notificationsActions from '../notifications/actions'
import panelsActions from '../panels/actions'
import storesActions from '../stores/actions'
import { selectIsInStoreUser } from './selectors'
import { slice } from './slice'

const fetchUser =
	(
		username: string,
		navigate: NavigateFunction,
	): AppThunkPromise<User | void> =>
	(dispatch: TeloDispatch, getState: TeloGetState) =>
		getUserByUsernameApi(username).then((user: ApiUser | void) => {
			if (!user) {
				return
			}
			dispatch(slice.actions._loadUser(user))
			if (!user.stores || !user.stores.length) {
				return user
			}
			user.stores.forEach(s => checkStoreIdFormat(s.store._id, 'fetchUser'))
			dispatch(storesActions._setStores(user.stores.map(s => s.store)))

			const state = getState()
			const selectedStore = selectStoreId(state)
			const practiceExternalId = selectStorePracticeExternalId(state)
			const inStoreUser = selectIsInStoreUser(state)
			const isAdminPage = window.location.pathname.match('admin')
			const isStoreNotMandatory = !inStoreUser || isAdminPage
			const inStoreCount = user.stores.length

			if (isStoreNotMandatory) {
				return user
			}

			if (inStoreCount > 1 && !selectedStore) {
				navigate('/store-selection', { state: { fromLogin: true } })
				return user
			} else if (!selectedStore) {
				dispatch(appActions.setStoreId(user.stores[0].store._id))
				dispatch(
					appActions.setPracticeExternalId(
						user.stores[0].store.externalIds.find(
							id => id.source === EXTERNAL_IDENTIFIER_SOURCE,
						)?.code || '',
					),
				)
			} else if (selectedStore && !practiceExternalId) {
				const storeCoop = user.stores.find(s => s.store._id === selectedStore)
				if (storeCoop) {
					dispatch(
						appActions.setPracticeExternalId(
							storeCoop.store.externalIds.find(
								id => id.source === EXTERNAL_IDENTIFIER_SOURCE,
							)?.code || '',
						),
					)
				}
			}

			return user
		})

const fetchByUsername =
	(username: string) => async (dispatch: TeloDispatch) => {
		const user = await getUserByUsernameApi(username)
		if (!user) {
			return
		}
		dispatch(slice.actions._loadUser(user))
	}

const fetchByUsernameAdmin =
	(username: string): AppThunk =>
	(dispatch: TeloDispatch) => {
		getUserByUsernameAdminApi(username).then((users: ApiUser[] | void) => {
			if (!users) {
				return
			}
			dispatch(slice.actions._loadUser(users[0]))
		})
	}

const fetchAllUsers = (): AppThunk => (dispatch: TeloDispatch) => {
	getAllUsers().then((users: ApiUser[] | void) => {
		if (!users || !users.length) {
			return
		}
		dispatch(slice.actions._loadUsers(users))
	})
}

const fetchStaffUsers =
	(storeId: string): AppThunk =>
	(dispatch: TeloDispatch) => {
		getStaffUsers(storeId).then((users: ApiUser[] | void) => {
			if (!users || !users.length) {
				return
			}
			dispatch(slice.actions._loadUsers(users))
		})
	}

const fetchPracticeUsers =
	(practiceId: string): AppThunk =>
	(dispatch: TeloDispatch) => {
		getPracticeUsersApi(practiceId).then((users: ApiUser[] | void) => {
			if (!users || !users.length) {
				return
			}
			dispatch(slice.actions._loadUsers(users))
		})
	}

const setUserAvailability =
	(userId: Id, available: boolean): AppThunk =>
	(dispatch: TeloDispatch, getState: TeloGetState) => {
		const state = getState()
		const currentSelectedStoreId = selectStoreId(state)
		const practiceExternalId = selectStorePracticeExternalId(state)
		setUserAvailabilityApi(userId, available).then((user: ApiUser | void) => {
			if (!user) {
				return
			}
			dispatch(slice.actions._loadUser(user))
			if (!user.stores || !user.stores.length) {
				return
			}
			dispatch(storesActions._setStores(user.stores.map(s => s.store)))
			if (
				!user.stores.some(s => s.store._id === currentSelectedStoreId) &&
				state.app.inStore
			) {
				dispatch(appActions.setStoreId(user.stores[0].store._id))
				dispatch(
					appActions.setPracticeExternalId(
						user.stores[0].store.externalIds.find(
							id => id.source === EXTERNAL_IDENTIFIER_SOURCE,
						)?.code || '',
					),
				)
			} else if (
				currentSelectedStoreId &&
				currentSelectedStoreId !== '0' &&
				!practiceExternalId
			) {
				const storeCooperation = user.stores.find(
					s => s.store._id === currentSelectedStoreId,
				)
				storeCooperation &&
					dispatch(
						appActions.setPracticeExternalId(
							storeCooperation.store.externalIds.find(
								id => id.source === EXTERNAL_IDENTIFIER_SOURCE,
							)?.code || '',
						),
					)
			}
		})
	}

const updateUser =
	(user: Partial<UpdateUserPayload>): AppThunk =>
	(dispatch: TeloDispatch) => {
		updateUserApi(user).then(
			(user: ApiUser | void) => user && dispatch(slice.actions._loadUser(user)),
		)
	}

const updateUserWithSignature =
	(_user: Partial<UpdateUserPayload>, signature?: string): AppThunk =>
	(dispatch: TeloDispatch) => {
		updateUserApi(_user)
			.then((user: ApiUser | void) => {
				if (user && user.signature && !signature)
					return deleteSignatureApi(user.username)
				else if (user && signature)
					return saveSignatureApi(user.username, signature)
				else return user
			})
			.then(
				(user: ApiUser | void) =>
					user && dispatch(slice.actions._loadUser(user)),
			)
	}

const updateUserIcds =
	(user: Partial<UpdateUserPayload>): AppThunk =>
	(dispatch: TeloDispatch) => {
		updateUserIcdsApi(user).then(
			(user: ApiUser | void) => user && dispatch(slice.actions._loadUser(user)),
		)
	}

const updateUserInStoreFlag =
	(user: { _id: string; loggedInAsInStore: boolean }): AppThunk =>
	dispatch => {
		updateUserInStoreFlagApi(user).then(savedUser => {
			dispatch(slice.actions._loadUser(savedUser))
			dispatch(
				appActions.setInStore(
					savedUser.loggedInAsInStore ?? user.loggedInAsInStore,
				),
			)
		})
	}

const createUser =
	(
		_user: CreateUserPayload,
		navigate: NavigateFunction,
		signature?: string,
	): AppThunkPromise =>
	(dispatch: TeloDispatch) => {
		return createUserApi(_user)
			.then((user: ApiUser | void) =>
				user && signature ? saveSignatureApi(user.username, signature) : user,
			)
			.then(
				(user: ApiUser | void) =>
					user && dispatch(slice.actions._loadUser(user)),
			)
			.then(() => {
				const proms = (_user.panels || []).map(panel =>
					dispatch(panelsActions.addDoctorInPanel(panel, _user.username)),
				)
				return Promise.all(proms)
			})
			.then(() => navigate('/admin?selectedTab=users'))
			.then(() =>
				dispatch(
					notificationsActions.addNotification({
						message: i18n.t('users.creationSuccess'),
						autoClose: true,
						type: 'success',
						autoCloseDelay: 3000,
					}),
				),
			)
	}

const deleteUser = (user: User) => (dispatch: TeloDispatch) =>
	deleteUserApi(user._id).then(numberOfUsersDeleted => {
		numberOfUsersDeleted === 1
			? dispatch(slice.actions._removeUser(user.username))
			: dispatch(
					notificationsActions.addNotification({
						type: 'error',
						message: `Delete error: 1 user had to be deleted, instead ${numberOfUsersDeleted} were deleted`,
						autoClose: false,
					}),
			  )
	})

const usersActions = {
	fetchUser,
	fetchByUsernameAdmin,
	fetchAllUsers,
	fetchStaffUsers,
	fetchPracticeUsers,
	setUserAvailability,
	updateUser,
	updateUserIcds,
	updateUserInStoreFlag,
	updateUserWithSignature,
	createUser,
	fetchByUsername,
	deleteUser,
}

export default usersActions
